/**
 * In-memory time series for every signal.
 *
 * The rendering strategy this is built for: React owns structure (which widgets
 * exist, which signals are listed) and nothing else. Gauges, charts and
 * sparklines read straight out of these ring buffers inside requestAnimationFrame,
 * so a 20 Hz data stream costs zero React re-renders.
 */
import type { SignalDef } from '../types.js';

/** Fixed-capacity circular buffer of timestamped samples. */
export class RingBuffer {
  private readonly times: Float64Array;
  private readonly values: Float64Array;
  private head = 0;
  private size = 0;

  constructor(readonly capacity: number) {
    this.times = new Float64Array(capacity);
    this.values = new Float64Array(capacity);
  }

  get length(): number {
    return this.size;
  }

  push(t: number, v: number): void {
    this.times[this.head] = t;
    this.values[this.head] = v;
    this.head = (this.head + 1) % this.capacity;
    if (this.size < this.capacity) this.size++;
  }

  /** Index 0 is the oldest retained sample. */
  timeAt(i: number): number {
    return this.times[(this.head - this.size + i + this.capacity * 2) % this.capacity] ?? 0;
  }

  valueAt(i: number): number {
    return this.values[(this.head - this.size + i + this.capacity * 2) % this.capacity] ?? 0;
  }

  latest(): { t: number; v: number } | undefined {
    if (this.size === 0) return undefined;
    const i = (this.head - 1 + this.capacity) % this.capacity;
    return { t: this.times[i] ?? 0, v: this.values[i] ?? 0 };
  }

  /**
   * Copy the newest `n` samples into caller-owned arrays, oldest first.
   * Returns how many were written. Lets hot render loops avoid allocating.
   */
  copyLast(n: number, outT: Float64Array, outV: Float64Array): number {
    const count = Math.min(n, this.size, outT.length, outV.length);
    const start = this.size - count;
    for (let i = 0; i < count; i++) {
      const idx = (this.head - this.size + start + i + this.capacity * 2) % this.capacity;
      outT[i] = this.times[idx] ?? 0;
      outV[i] = this.values[idx] ?? 0;
    }
    return count;
  }

  /**
   * The sample nearest in time to `t`, if one lies within `maxGapMs`. Used to
   * line up readings that arrive on different clocks, such as GPS fixes.
   */
  valueNear(t: number, maxGapMs: number): number | undefined {
    let best: number | undefined;
    let bestGap = maxGapMs;
    // Newest first: callers almost always ask about the recent past.
    for (let i = this.size - 1; i >= 0; i--) {
      const ti = this.timeAt(i);
      const gap = Math.abs(ti - t);
      if (gap <= bestGap) {
        bestGap = gap;
        best = this.valueAt(i);
      } else if (ti < t - bestGap) {
        break;
      }
    }
    return best;
  }

  /**
   * The value at time `t`, interpolated between the samples either side of
   * it. Undefined when `t` is outside the history or the samples around it
   * are more than `maxSpanMs` apart.
   */
  interpolate(t: number, maxSpanMs: number): number | undefined {
    for (let i = this.size - 1; i >= 0; i--) {
      const ti = this.timeAt(i);
      if (ti > t) continue;
      if (ti === t) return this.valueAt(i);
      if (i === this.size - 1) return undefined;
      const tj = this.timeAt(i + 1);
      if (tj - ti > maxSpanMs) return undefined;
      const vi = this.valueAt(i);
      return vi + ((this.valueAt(i + 1) - vi) * (t - ti)) / (tj - ti);
    }
    return undefined;
  }

  /** Min and max over the newest `n` samples, for autoscaling a plot. */
  extent(n: number): { min: number; max: number } | undefined {
    const count = Math.min(n, this.size);
    if (count === 0) return undefined;
    let min = Infinity;
    let max = -Infinity;
    const start = this.size - count;
    for (let i = 0; i < count; i++) {
      const v = this.valueAt(start + i);
      if (v < min) min = v;
      if (v > max) max = v;
    }
    return { min, max };
  }

  clear(): void {
    this.head = 0;
    this.size = 0;
  }
}

export interface SignalStats {
  min: number;
  max: number;
  /** Time-weighted mean over the session. */
  mean: number;
  samples: number;
  lastUpdate: number;
  /** Successful reads per second, smoothed. Shows the true achieved rate. */
  hz: number;
}

export interface SignalState {
  def: SignalDef;
  /** True once the vehicle has actually answered for this signal. */
  available: boolean;
  value: number | undefined;
  history: RingBuffer;
  stats: SignalStats;
}

const DEFAULT_CAPACITY = 1800; // 90 s at 20 Hz, or 30 min at 1 Hz

export class SignalStore {
  private readonly states = new Map<string, SignalState>();
  private readonly structureListeners = new Set<() => void>();
  /** Bumped on every committed batch; lets render loops skip idle frames. */
  private frameCounter = 0;

  constructor(private readonly capacity = DEFAULT_CAPACITY) {}

  get frame(): number {
    return this.frameCounter;
  }

  /** Make a signal known to the app. Idempotent. */
  register(def: SignalDef): SignalState {
    const existing = this.states.get(def.key);
    if (existing) return existing;
    const state: SignalState = {
      def,
      available: false,
      value: undefined,
      history: new RingBuffer(this.capacity),
      stats: { min: Infinity, max: -Infinity, mean: 0, samples: 0, lastUpdate: 0, hz: 0 },
    };
    this.states.set(def.key, state);
    this.notifyStructure();
    return state;
  }

  has(key: string): boolean {
    return this.states.has(key);
  }

  get(key: string): SignalState | undefined {
    return this.states.get(key);
  }

  value(key: string): number | undefined {
    return this.states.get(key)?.value;
  }

  /** Current value, or `fallback` when the signal has never reported. */
  valueOr(key: string, fallback: number): number {
    const v = this.states.get(key)?.value;
    return v === undefined || Number.isNaN(v) ? fallback : v;
  }

  list(): SignalState[] {
    return [...this.states.values()];
  }

  /** Only the signals the vehicle has actually answered for. */
  available(): SignalState[] {
    return [...this.states.values()].filter((s) => s.available);
  }

  /**
   * Record a new sample. Does not notify listeners -- call `commit()` once per
   * batch so a six-PID response produces one notification, not six.
   */
  update(key: string, value: number | undefined, t = Date.now()): void {
    const s = this.states.get(key);
    if (!s || value === undefined || !Number.isFinite(value)) return;

    const prev = s.stats.lastUpdate;
    s.value = value;
    s.available = true;
    s.history.push(t, value);

    const st = s.stats;
    if (value < st.min) st.min = value;
    if (value > st.max) st.max = value;
    st.samples++;
    st.mean += (value - st.mean) / st.samples;
    st.lastUpdate = t;
    if (prev > 0 && t > prev) {
      const instantHz = 1000 / (t - prev);
      // Exponential smoothing keeps the displayed rate readable.
      st.hz = st.hz === 0 ? instantHz : st.hz * 0.8 + instantHz * 0.2;
    }
  }

  /** Signal the end of an update batch. */
  commit(): void {
    this.frameCounter++;
  }

  /** Mark a signal as confirmed present even before the first value arrives. */
  markAvailable(key: string): void {
    const s = this.states.get(key);
    if (s && !s.available) {
      s.available = true;
      this.notifyStructure();
    }
  }

  resetStats(): void {
    for (const s of this.states.values()) {
      s.stats = { min: Infinity, max: -Infinity, mean: 0, samples: 0, lastUpdate: 0, hz: 0 };
      s.history.clear();
    }
    this.notifyStructure();
  }

  /**
   * Subscribe to structural changes only -- signals appearing or disappearing.
   * Value changes deliberately do not fire here.
   */
  onStructureChange(cb: () => void): () => void {
    this.structureListeners.add(cb);
    return () => this.structureListeners.delete(cb);
  }

  private notifyStructure(): void {
    for (const cb of this.structureListeners) cb();
  }
}
